import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Heart, Menu, Search, ShoppingBag, User, X, MoreVertical } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { useWishlist } from "../context/WishlistContext";
import { CATEGORIES } from "../lib/utils-cynos";

const primaryCategories = CATEGORIES.filter((category) => ["unisex", "oversized", "graphic", "anime"].includes(category.slug));

const navBaseClass = "text-[11px] uppercase tracking-[0.16em] font-mono transition-colors";
const navTextClass = (active) => `${navBaseClass} ${active ? "text-cynos-red" : "text-white hover:text-cynos-red"}`;
const navLinkClass = ({ isActive }) => navTextClass(isActive);

const mobileLinkClass = "flex min-h-[46px] items-center rounded-[8px] px-3 font-mono text-[13px] uppercase tracking-[0.16em] text-white transition hover:bg-white/5 hover:text-cynos-red";

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { totalQty, setOpen } = useCart();
  const { totalCount: wishlistCount } = useWishlist();
  const { customer, isAuthenticated, logout } = useCustomerAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [desktopSearchTerm, setDesktopSearchTerm] = useState("");
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const mobileMenuRef = useRef(null);
  const mobileToggleRef = useRef(null);
  const moreMenuTabletRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onPointerDown = (event) => {
      if (mobileMenuRef.current && mobileMenuRef.current.contains(event.target)) return;
      if (mobileToggleRef.current && mobileToggleRef.current.contains(event.target)) return;
      setMobileOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, [mobileOpen]);

  // Close more menu when clicking outside
  useEffect(() => {
    if (!moreMenuOpen) return undefined;

    const onPointerDown = (event) => {
      if (moreMenuTabletRef.current && moreMenuTabletRef.current.contains(event.target)) return;
      setMoreMenuOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [moreMenuOpen]);

  const submitSearch = (event, term) => {
    event.preventDefault();
    const searchValue = term.trim();
    setMobileOpen(false);
    navigate(searchValue ? `/shop?search=${encodeURIComponent(searchValue)}` : "/shop");
  };

  const closeMobile = () => setMobileOpen(false);
  const currentCategory = new URLSearchParams(location.search).get("category") || "all";
  const isShopPath = location.pathname === "/shop";
  const isShopAllActive = isShopPath && (currentCategory === "all" || !currentCategory);
  const isCategoryActive = (slug) => isShopPath && currentCategory === slug;
  
  // Render desktop search bar
  const renderDesktopSearch = () => (
    <form
      onSubmit={(e) => submitSearch(e, desktopSearchTerm)}
      className="hidden min-h-[42px] w-[190px] items-center gap-2 rounded-[8px] border border-white/15 bg-white/[0.06] px-3 transition focus-within:border-white/40 md:flex lg:w-[280px]"
      data-testid="nav-search-form"
    >
      <Search size={16} className="shrink-0 text-zinc-500" />
      <input
        value={desktopSearchTerm}
        onChange={(event) => setDesktopSearchTerm(event.target.value)}
        placeholder="Search drops"
        className="min-w-0 flex-1 bg-transparent font-mono text-[11px] uppercase tracking-[0.14em] text-white outline-none placeholder:text-zinc-600"
        data-testid="nav-search-input"
      />
    </form>
  );
  
  // Render navigation links
  const renderNavLinks = (mobile = false) => (
    <>
      <Link
        to="/shop"
        onClick={mobile ? closeMobile : undefined}
        className={mobile ? mobileLinkClass : navTextClass(isShopAllActive)}
        data-testid="nav-shop"
      >
        Shop
      </Link>
      {primaryCategories.map((category) => (
        <Link
          key={category.slug}
          to={`/shop?category=${category.slug}`}
          onClick={mobile ? closeMobile : undefined}
          className={mobile ? mobileLinkClass : navTextClass(isCategoryActive(category.slug))}
          data-testid={`nav-cat-${category.slug}`}
        >
          {category.label}
        </Link>
      ))}
      <NavLink
        to="/custom-design"
        onClick={mobile ? closeMobile : undefined}
        className={mobile ? mobileLinkClass : navLinkClass}
        data-testid="nav-custom-design"
      >
        Custom
      </NavLink>
      <NavLink
        to="/blog"
        onClick={mobile ? closeMobile : undefined}
        className={mobile ? mobileLinkClass : navLinkClass}
        data-testid="nav-blog"
      >
        Blog
      </NavLink>
    </>
  );

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-40 border-b backdrop-blur-2xl transition-all ${
        scrolled ? "border-white/10 bg-black/90 shadow-[0_12px_34px_rgba(0,0,0,0.35)]" : "border-transparent bg-black/45"
      }`}
    >
      {/* Desktop & Tablet Navigation - visible on md+ */}
      <div className="hidden md:flex min-h-[70px] items-center justify-between gap-6 px-5 lg:min-h-[90px] lg:px-12 xl:px-16">
        <Link to="/" className="h-[50px] md:h-[70px] lg:h-[90px] flex-shrink-0" style={{ width: 'auto', minWidth: '100px' }} data-testid="brand-logo">
          <img src="/main_logo_header.png" alt="CYNOS" className="h-full w-auto" />
        </Link>

        <nav className="flex items-center gap-3 lg:gap-5 xl:gap-7" aria-label="Primary navigation">
          {renderNavLinks(false)}
          
          {/* Additional Links for Desktop (lg+) */}
          <NavLink to="/track-order" className="hidden lg:block text-[11px] uppercase tracking-[0.16em] font-mono transition-colors text-white hover:text-cynos-red" data-testid="nav-track-order">
            Track Order
          </NavLink>
          <NavLink to="/shipping-returns" className="hidden lg:block text-[11px] uppercase tracking-[0.16em] font-mono transition-colors text-white hover:text-cynos-red" data-testid="nav-shipping">
            Shipping
          </NavLink>
          <NavLink to="/contact" className="hidden lg:block text-[11px] uppercase tracking-[0.16em] font-mono transition-colors text-white hover:text-cynos-red" data-testid="nav-contact">
            Contact
          </NavLink>

          {/* Three-dot menu for tablet (md to lg) */}
          <div className="relative lg:hidden" ref={moreMenuTabletRef}>
            <button
              type="button"
              className="inline-flex h-9 w-9 items-center justify-center rounded-[6px] text-white transition hover:bg-white/5"
              onClick={() => setMoreMenuOpen((value) => !value)}
              aria-label="More options"
              aria-expanded={moreMenuOpen}
              data-testid="nav-tablet-more-menu"
            >
              <MoreVertical size={18} />
            </button>

            {/* Dropdown Menu for Tablet */}
            {moreMenuOpen && (
              <div className="absolute right-0 top-[40px] w-44 rounded-[8px] border border-white/10 bg-black shadow-xl z-50">
                <NavLink
                  to="/track-order"
                  onClick={() => setMoreMenuOpen(false)}
                  className={({ isActive }) =>
                    `block px-3 py-2 text-xs font-mono uppercase tracking-[0.14em] rounded-t-[8px] transition ${
                      isActive ? "bg-white/10 text-cynos-red" : "text-white hover:bg-white/5 hover:text-cynos-red"
                    }`
                  }
                  data-testid="nav-tablet-track"
                >
                  Track Order
                </NavLink>
                <NavLink
                  to="/shipping-returns"
                  onClick={() => setMoreMenuOpen(false)}
                  className={({ isActive }) =>
                    `block px-3 py-2 text-xs font-mono uppercase tracking-[0.14em] transition border-t border-white/10 ${
                      isActive ? "bg-white/10 text-cynos-red" : "text-white hover:bg-white/5 hover:text-cynos-red"
                    }`
                  }
                  data-testid="nav-tablet-shipping"
                >
                  Shipping
                </NavLink>
                <NavLink
                  to="/contact"
                  onClick={() => setMoreMenuOpen(false)}
                  className={({ isActive }) =>
                    `block px-3 py-2 text-xs font-mono uppercase tracking-[0.14em] rounded-b-[8px] transition border-t border-white/10 ${
                      isActive ? "bg-white/10 text-cynos-red" : "text-white hover:bg-white/5 hover:text-cynos-red"
                    }`
                  }
                  data-testid="nav-tablet-contact"
                >
                  Contact
                </NavLink>
              </div>
            )}
          </div>
        </nav>

        <div className="flex items-center gap-2 lg:gap-3">
          {renderDesktopSearch()}

          <NavLink
            to="/wishlist"
            className="relative inline-flex h-11 w-11 items-center justify-center rounded-[8px] text-white transition hover:bg-white/5 hover:text-cynos-red"
            aria-label="Wishlist"
            data-testid="nav-wishlist"
          >
            <Heart size={20} />
            {wishlistCount > 0 && (
              <span className="absolute -right-1 -top-1 min-w-5 bg-cynos-red px-1.5 py-0.5 text-center font-mono text-[10px] text-white">
                {wishlistCount}
              </span>
            )}
          </NavLink>

          {isAuthenticated ? (
            <NavLink to="/profile" className="min-h-[42px] items-center gap-2 rounded-[8px] border border-white/10 bg-white/5 px-3 text-sm text-white transition hover:border-cynos-red hover:text-cynos-red inline-flex" data-testid="nav-profile">
              <User size={16} />
              <span className="max-w-[120px] truncate hidden lg:inline">{customer?.name || "Profile"}</span>
              <span className="inline lg:hidden">Profile</span>
            </NavLink>
          ) : (
            <NavLink to="/signin" className="min-h-[42px] items-center rounded-[8px] border border-white/10 bg-white/5 px-4 font-mono text-[11px] uppercase tracking-[0.16em] text-white transition hover:border-cynos-red hover:text-cynos-red inline-flex">
              Sign In
            </NavLink>
          )}

          <button
            type="button"
            className="relative inline-flex h-11 w-11 items-center justify-center rounded-[8px] text-white transition hover:bg-white/5 hover:text-cynos-red"
            onClick={() => setOpen(true)}
            aria-label="Cart"
            data-testid="nav-cart-btn"
          >
            <ShoppingBag size={20} />
            {totalQty > 0 && (
              <span className="absolute -right-1 -top-1 min-w-5 bg-cynos-red px-1.5 py-0.5 text-center font-mono text-[10px] text-white">
                {totalQty}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Navigation - visible only on small screens */}
      <div className="grid md:hidden min-h-[65px] grid-cols-[44px_minmax(0,1fr)_auto] items-center gap-2 px-3 py-2">
        <button
          ref={mobileToggleRef}
          type="button"
          className="inline-flex h-11 w-11 items-center justify-center rounded-[8px] text-white transition hover:bg-white/5"
          onClick={() => setMobileOpen((value) => !value)}
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          aria-controls="mobile-nav-panel"
          data-testid="nav-mobile-toggle"
        >
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>

        <Link to="/" className="h-[50px] justify-self-center" style={{ width: "auto", minWidth: "80px" }} data-testid="brand-logo">
          <img src="/main_logo_header.png" alt="CYNOS" className="h-full w-auto" />
        </Link>

        <div className="flex items-center justify-end gap-1">
          <NavLink
            to="/wishlist"
            className="relative inline-flex h-11 w-11 items-center justify-center rounded-[8px] text-white transition hover:bg-white/5 hover:text-cynos-red"
            aria-label="Wishlist"
            data-testid="nav-wishlist"
          >
            <Heart size={20} />
            {wishlistCount > 0 && (
              <span className="absolute -right-1 -top-1 min-w-4 bg-cynos-red px-1 py-0.5 text-center font-mono text-[8px] text-white">
                {wishlistCount}
              </span>
            )}
          </NavLink>

          {isAuthenticated ? (
            <NavLink
              to="/profile"
              className="inline-flex h-11 w-11 items-center justify-center rounded-[8px] text-white transition hover:bg-white/5 hover:text-cynos-red"
              aria-label="Profile"
              data-testid="nav-profile-mobile"
            >
              <User size={20} />
            </NavLink>
          ) : (
            <NavLink
              to="/signin"
              className="inline-flex h-11 w-11 items-center justify-center rounded-[8px] text-white transition hover:bg-white/5 hover:text-cynos-red"
              aria-label="Sign In"
              data-testid="nav-signin-mobile"
            >
              <User size={20} />
            </NavLink>
          )}

          <button
            type="button"
            className="relative inline-flex h-11 w-11 items-center justify-center rounded-[8px] text-white transition hover:bg-white/5 hover:text-cynos-red"
            onClick={() => setOpen(true)}
            aria-label="Cart"
            data-testid="nav-cart-btn"
          >
            <ShoppingBag size={20} />
            {totalQty > 0 && (
              <span className="absolute -right-1 -top-1 min-w-4 bg-cynos-red px-1 py-0.5 text-center font-mono text-[8px] text-white">
                {totalQty}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu Drawer */}
      {mobileOpen && (
        <div className="fixed inset-x-0 top-[65px] h-[calc(100svh-65px)] md:hidden">
          <button type="button" aria-label="Close menu" className="absolute inset-0 bg-black/70" onClick={closeMobile} />
          <div
            ref={mobileMenuRef}
            id="mobile-nav-panel"
            className="absolute inset-y-0 left-0 w-[min(88vw,380px)] overflow-y-auto border-r border-white/10 bg-black p-3 shadow-2xl"
          >
            {/* Mobile Search */}
            <form onSubmit={(e) => submitSearch(e, searchTerm)} className="mb-3 flex min-h-[48px] items-center gap-3 rounded-[8px] border border-white/15 bg-cynos-surface px-3" data-testid="mnav-search-form">
              <Search size={16} className="text-zinc-500" />
              <input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search drops"
                className="min-w-0 flex-1 bg-transparent font-mono text-xs uppercase tracking-[0.16em] outline-none placeholder:text-zinc-600"
                data-testid="mnav-search-input"
              />
            </form>

            {/* Mobile Navigation Links */}
            <nav className="flex flex-col gap-1">
              {renderNavLinks(true)}
            </nav>

            <div className="my-3 border-t border-white/10" />

            {/* Additional Mobile Menu Items */}
            <Link to="/track-order" onClick={closeMobile} className={mobileLinkClass} data-testid="mnav-track-order">
              Track Order
            </Link>
            <Link to="/shipping-returns" onClick={closeMobile} className={mobileLinkClass} data-testid="mnav-shipping">
              Shipping & Returns
            </Link>
            <Link to="/contact" onClick={closeMobile} className={mobileLinkClass} data-testid="mnav-contact">
              Contact
            </Link>

            <div className="my-3 border-t border-white/10" />

            {/* Mobile Auth Section */}
            {isAuthenticated ? (
              <>
                <Link to="/profile" onClick={closeMobile} className={`${mobileLinkClass} gap-2`} data-testid="mnav-profile">
                  <User size={16} /> Profile
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    logout();
                    closeMobile();
                  }}
                  className={`${mobileLinkClass} w-full text-left`}
                  data-testid="mnav-logout"
                >
                  Logout
                </button>
              </>
            ) : (
              <Link to="/signin" onClick={closeMobile} className={mobileLinkClass} data-testid="mnav-signin">
                Sign In
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
